import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, TextField, Button, Box, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { loginApi, saveToken, saveUser } from '../core/services/authService';
import { useDispatch } from 'react-redux';
import { LOGIN } from '../core/store/slice/authSlice';
import { useNavigate } from 'react-router-dom';
import { styled } from '@mui/system';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import LogoutIcon from '@mui/icons-material/Logout';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import Super_admin from '../../public/assets/logo-admin.png';
import { motion } from 'framer-motion';
import heroBg from '../../public/assets/dashboard-image.jpeg';
import logoIcon from '../../public/assets/Convrt_bg_remover.png';

// Styled components for the left and right sections
const LeftSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: 'white',
    padding: '40px',
    height: '100vh',
    // marginTop:'8px',
    // marginLeft:'8px',
    backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.53)), url(${heroBg})`,
    backgroundSize: 'cover',
    // borderRadius:'12px',
    backgroundPosition: 'center',
    // borderBottomRightRadius: '13%',
    // borderTopRightRadius: '13%',
    borderRight: 'inset',
}));

const RightSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    padding: '40px',

    [theme.breakpoints.down('md')]: {
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px',

    },
}));

// Container styling for the form
const FormContainer = styled(Paper)({
    padding: '30px',
    maxWidth: '400px',
    width: '100%',
    borderRadius: '22px',
    boxShadow: '0px 5px 15px rgba(0,0,0,0.2)',
});

const MotionTextField = motion(TextField);
const MotionButton = motion(Button);

const validationSchema = yup.object({
    email: yup.string('Enter your email').email('Enter a valid email').required('Email is required'),
    password: yup.string('Enter your password').required('Password is required'),
});

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [version, setVersion] = useState('');

    useEffect(() => {
        const fetchVersion = async () => {
            try {
                
                const response = await axios.get('/iconnctAdminPanel/version.json');
                setVersion(response.data.version);
            } catch (error) {
                console.error('Error fetching version:', error);
            }
        };
        fetchVersion();
    }, []);

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                const deviceOS = 'web';
                setLoading(true);
                const res = await loginApi(values.email, values.password, deviceOS);
                if (res?.status === 200) {
                    if (['admin', 'organization_admin'].includes(res?.data?.user?.user_type)) {
                        saveToken(res?.data?.token);
                        saveUser(res?.data);
                        dispatch(LOGIN(res?.data));
                        navigate('/');
                        toast.success(res.data.message);
                    } else if(res?.data?.user?.user_type === 'isa_agent'){
                        saveToken(res?.data?.token);
                        saveUser(res?.data);
                        dispatch(LOGIN(res?.data));
                        navigate('/');
                        toast.success(res.data.message);

                    }else if(res?.data?.user?.user_type === 'sales'){
                        saveToken(res?.data?.token);
                        saveUser(res?.data);
                        dispatch(LOGIN(res?.data));
                        navigate('/');
                        toast.success(res.data.message);
                    }else {
                        toast.error('Invalid user.');
                    }
                }
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <Grid container>
            {/* Left Section with Background Image and Text */}
            <Grid item xs={12} sm={6} md={6} lg={6}>
                <LeftSection>
                    <img src={logoIcon} alt="" style={{ width: "270px", height: '270px' }}></img>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                        Linking Agents, Buyers, and Homes
                    </Typography>
                    <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 5, textAlign: 'center' }}>
                        Simplifying the process for a better experience.
                    </Typography>
                </LeftSection>
            </Grid>

            {/* Right Section with Login Form */}
            <Grid item xs={12} sm={6} md={6} lg={6} >
                <RightSection>
                    <FormContainer elevation={3}>
                        <Typography variant="h5" component="h2" sx={{ textAlign: 'center', fontWeight: 600, mb: 1 }}>
                            Welcome to iCONNCT!
                        </Typography>
                        <Box style={{ marginTop: '1px', display: 'flex', justifyContent: 'center' }}>
                            <img src={Super_admin} alt="" style={{ width: '230px', height: '60px' }} />
                        </Box>
                        <Typography variant="body2" sx={{ textAlign: 'center', mb: 4 }}>
                            Please login to your account to continue
                        </Typography>

                        {/* Login Form */}
                        <form onSubmit={formik.handleSubmit}>
                            <MotionTextField
                                id="email"
                                name="email"
                                label="Email Address"
                                variant="outlined"
                                fullWidth
                                sx={{
                                    mb: 2,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                    },
                                }}
                                value={formik.values.email}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.email && Boolean(formik.errors.email)}
                                helperText={formik.touched.email && formik.errors.email}
                                whileFocus={{ scale: 1.05 }}
                                whileHover={{ scale: 1.02 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            />
                            <MotionTextField
                                id="password"
                                name="password"
                                label="Password"
                                variant="outlined"
                                fullWidth
                                sx={{
                                    mb: 4,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                    },
                                }}
                                type={showPassword ? 'text' : 'password'}
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClickShowPassword} edge="end">
                                                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                whileFocus={{ scale: 1.05 }}
                                whileHover={{ scale: 1.02 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            />
                            <MotionButton variant="contained" color="primary" fullWidth type="submit"
                                sx={{ p: 1.0, fontSize: '16px', display: 'flex', justifyContent: 'center', borderRadius: '12px',}}>

                                {loading ? (
                                    <>
                                        <Typography variant="body2" > Checking Credentials{' '} </Typography><LockOpenIcon />

                                    </>
                                ) : !formik.values.email || !formik.values.password ? (
                                    <>
                                        <Typography variant="body2" > Fill Details{' '} </Typography><SentimentVeryDissatisfiedIcon />

                                    </>
                                ) : (
                                    <>
                                        <Typography variant="body2" > Login{' '} </Typography><LogoutIcon />

                                    </>
                                )}

                            </MotionButton>
                            <LinearProgress color="success" sx={{ visibility: loading ? '' : 'hidden' , }} />
                        </form>
                        <Box style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
                            <div style={{display:'grid',gap:'20px'}}>
                            {/* <Typography onClick={() => navigate('/sales-login')} variant="body2" sx={{
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: '#155fa8'
                                },
                            }}>
                                Login as Sales
                            </Typography> */}
                            <Typography style={{ fontWeight: 'bold' }}>
                                Version: <span style={{ color: '#4c6ced' }}>{version}</span>
                            </Typography>
                            </div>
                            {/* <Typography onClick={() => navigate('/ica-login')} variant="body2" sx={{
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: '#155fa8'
                                },
                            }}>
                                Login as ICA
                            </Typography> */}
                        </Box>
                    </FormContainer>
                </RightSection>
            </Grid>
        </Grid>
    );
};

export default Login;
